<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useScheduleStore } from '@/stores/schedule.store'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'
import { useTaskActions, useTaskLoader, useTaskServiceOptions, projectMissingServiceIds } from '@/composables/tasks'

import MissingServicesWarning from '@/components/task/MissingServicesWarning.vue'
import TaskEdit from '@/components/task/TaskEdit.vue'
import TaskGroups from '@/components/task/TaskGroups.vue'

const props = defineProps({
  projectId: {
    type: Number,
    required: true,
  },
})

const i18n = getI18n()
const scheduleStore = useScheduleStore()
const { isMobile } = useDetectDevice()
const { ensureServicesAsync } = useTaskServiceOptions()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const container = ref(null)
const editTask = ref(null)
const collapsedGroups = ref([])

const { tasks, loadAsync } = useTaskLoader(() => scheduleStore.fetchTasksAsync({ project: props.projectId }), {
  onNewTaskAsync: (taskId) => highlightRowAsync(taskId, container),
})

const missingServiceIds = computed(() => projectMissingServiceIds(tasks.value.filter(task => !task.completed)))

const { toggleCompletedAsync, newTaskDefaults } = useTaskActions()

const addTask = () => {
  editTask.value = newTaskDefaults(props.projectId)
}

onMounted(() => Promise.all([scheduleStore.ensureTaskTypesAsync(), ensureServicesAsync(), loadAsync()]))

defineExpose({ addTask })
</script>

<template>
  <div ref="container" class="column">
    <div class="row justify-end items-center q-px-md">
      <q-btn
        style="padding: 0;"
        size="md"
        unelevated
        ripple
        rounded
        icon="mdi-plus-circle-outline"
        @click="addTask"
      />
    </div>
    <missing-services-warning class="q-mb-xs" :service-ids="missingServiceIds" />
    <task-groups
      v-if="tasks.length"
      v-model:collapsed="collapsedGroups"
      :tasks="tasks"
      split-by-type
      :highlighted-id="highlightedRowId"
      @toggle="toggleCompletedAsync"
      @open="editTask = $event"
    />
    <div v-else class="text-grey-7 text-center q-pa-lg">{{ i18n.t('common.tasks.noTasks') }}</div>

    <q-dialog
      :model-value="editTask !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="editTask = null"
    >
      <task-edit
        v-if="editTask !== null"
        :model-value="editTask"
        @update="editTask = null"
        @cancel="editTask = null"
      />
    </q-dialog>
  </div>
</template>
